import React from "react";
import CelebrationIcon from '@mui/icons-material/Celebration';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import BarChartIcon from '@mui/icons-material/BarChart';

export default function GameSummary({
    kidName,
    totalPoints,
    personalBest,
    perfectScore,
    maxStreak,
    score,
    totalWords,
    attempts,
    leaderboard
}) {
    return (
        <>
            <div className="summary">
                <h2 className="summary-title">
                    <CelebrationIcon className="icon-md" />
                    Well done, {kidName}!
                </h2>

                {/* Score Comparison */}
                <div className={`score-comparison ${
                    totalPoints > personalBest ? 'new-best' :
                    totalPoints === personalBest && personalBest > 0 ? 'matched-best' :
                    'below-best'
                }`}>
                    <div className="score-comparison-points">
                        {totalPoints} pts
                    </div>
                    <div className="score-comparison-message">
                        {totalPoints > personalBest && personalBest > 0 && (
                            <span className="score-message-content">
                                <CelebrationIcon className="icon-sm" />
                                NEW PERSONAL BEST!
                                <CelebrationIcon className="icon-sm" />
                                <br/>Previous: {personalBest} pts
                            </span>
                        )}
                        {totalPoints === personalBest && personalBest > 0 && (
                            <span className="score-message-content">
                                <LocalFireDepartmentIcon className="icon-sm" />
                                Matched your best! Can you beat it?
                            </span>
                        )}
                        {totalPoints < personalBest && (
                            <span className="score-message-content">
                                <TrendingUpIcon className="icon-sm" />
                                Keep trying! Your best: {personalBest} pts
                            </span>
                        )}
                        {personalBest === 0 && (
                            <span className="score-message-content">
                                <StarIcon className="icon-sm" />
                                First attempt recorded!
                            </span>
                        )}
                    </div>
                    <div className="score-comparison-percentage">
                        {((totalPoints / perfectScore) * 100).toFixed(1)}% of perfect score ({perfectScore} pts)
                    </div>
                </div>

                {/* Achievements & Goals */}
                <div className="achievements-container">
                    <h3 className="achievements-title">
                        <EmojiEventsIcon className="icon-md" />
                        Stats & Achievements
                    </h3>
                    <p className="achievement-stat">
                        <LocalFireDepartmentIcon className="icon-sm stat-icon streak" />
                        Best Streak: {maxStreak} words
                    </p>
                    <p className="achievement-stat">
                        <CheckCircleIcon className="icon-sm stat-icon correct" />
                        Correct: {score} / {totalWords} words
                    </p>
                    <p className="achievement-stat">
                        <GpsFixedIcon className="icon-sm stat-icon attempts" />
                        Total Attempts: {attempts}
                    </p>
                    <p className="achievement-stat">
                        <BarChartIcon className="icon-sm stat-icon accuracy" />
                        Accuracy: {attempts > 0 ? ((score / attempts) * 100).toFixed(1) : 0}%
                    </p>

                    {/* Achievement Badges */}
                    <div className="achievement-badges">
                        {score === totalWords && (
                            <span className="achievement-badge badge-perfect">
                                <AutoAwesomeIcon className="icon-xs" /> Perfect Spelling
                            </span>
                        )}
                        {maxStreak >= 10 && (
                            <span className="achievement-badge badge-streak">
                                <LocalFireDepartmentIcon className="icon-xs" /> Hot Streak (10+)
                            </span>
                        )}
                        {attempts > 0 && ((score / attempts) * 100) === 100 && (
                            <span className="achievement-badge badge-first-try">
                                <GpsFixedIcon className="icon-xs" /> 100% First Try
                            </span>
                        )}
                        {totalPoints >= perfectScore * 0.9 && (
                            <span className="achievement-badge badge-high-score">
                                <StarIcon className="icon-xs" /> 90%+ Score
                            </span>
                        )}
                    </div>
                </div>

                {/* Next Steps - Motivational */}
                <div className="next-challenge">
                    <h3 className="next-challenge-title">
                        <GpsFixedIcon className="icon-md" />
                        Next Challenge
                    </h3>
                    {totalPoints >= perfectScore ? (
                        <p className="next-challenge-message">
                            <EmojiEventsIcon className="icon-sm" />
                            PERFECT SCORE! You're a spelling champion!
                        </p>
                    ) : totalPoints >= perfectScore * 0.9 ? (
                        <p className="next-challenge-message">So close to perfection! Try to get all words on first try with a long streak!</p>
                    ) : totalPoints > personalBest ? (
                        <p className="next-challenge-message">New record! Can you beat {totalPoints} points next time?</p>
                    ) : (
                        <p className="next-challenge-message">Challenge yourself to beat {personalBest > 0 ? personalBest : totalPoints} points! Focus on first-try answers and building streaks.</p>
                    )}
                    <div className="next-challenge-tip">
                        <TipsAndUpdatesIcon className="icon-sm" />
                        Tip: Build a streak of 10+ words for 5x multiplier!
                    </div>
                </div>
            </div>

            <h2 className="leaderboard-title">
                <EmojiEventsIcon className="icon-md" />
                Weekly Leaderboard
            </h2>
            <table className="leaderboard">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Week</th>
                        <th>Score</th>
                        <th>Attempts</th>
                    </tr>
                </thead>
                <tbody>
                    {leaderboard.map((entry, index) => (
                        <tr key={index}>
                            <td>{entry.name}</td>
                            <td>{entry.weekYear}</td>
                            <td>{entry.score}</td>
                            <td>{entry.attempts}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}
